import { useState, useEffect, useRef } from "react";
import {
  Link2,
  MessageCircle,
  BrainCircuit,
  CalendarCheck,
  ArrowDown,
  Sparkles,
} from "lucide-react";

const steps = [
  {
    icon: Link2,
    num: "01",
    title: "Share your link",
    desc: "One link replaces contact forms, DMs, and cold emails.",
    detail: "reachmenow.app/yourname",
    color: "from-violet-500 to-violet-600",
  },
  {
    icon: MessageCircle,
    num: "02",
    title: "AI responds instantly",
    desc: "Visitors get a human-feeling reply in under 3 seconds.",
    detail: '"Hi! Let me help you get started..."',
    color: "from-violet-600 to-fuchsia-500",
  },
  {
    icon: BrainCircuit,
    num: "03",
    title: "Qualifies & filters",
    desc: "Budget, timeline, and fit are sorted before you need to step in.",
    detail: "Lead score: 92/100",
    color: "from-fuchsia-600 to-violet-600",
  },
  {
    icon: CalendarCheck,
    num: "04",
    title: "Books or routes",
    desc: "Good fit? Booked. Bad fit? Politely declined.",
    detail: "Meeting scheduled",
    color: "from-violet-600 to-fuchsia-600",
  },
];

export default function SolutionSection() {
  const [activeStep, setActiveStep] = useState(0);
  const [started, setStarted] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) setStarted(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [started]);

  return (
    <section
      id="solution"
      ref={sectionRef}
      data-testid="solution-section"
      className="relative py-14 sm:py-[4.25rem]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="reveal text-center mb-10 sm:mb-11">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-violet-300/55 mb-3">
            How it works
          </p>
          <h2
            data-testid="solution-headline"
            className="text-3xl sm:text-4xl tracking-tight font-semibold mb-3"
          >
            One link.{" "}
            <span className="bg-gradient-to-r from-violet-300 via-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
              Everything handled.
            </span>
          </h2>
          <p className="text-sm text-violet-200/35 max-w-md mx-auto">
            From first message to a booked call, fully automated.
          </p>
        </div>

        <div className="reveal max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 items-start">
          {/* Steps list */}
          <div className="space-y-3">
            {steps.map((step, i) => (
              <button
                type="button"
                key={step.num}
                data-testid={`solution-step-${i + 1}`}
                onClick={() => setActiveStep(i)}
                aria-pressed={activeStep === i}
                aria-label={`Step ${i + 1}: ${step.title}`}
                className={`w-full text-left group relative flex items-start gap-4 sm:gap-5 p-4 sm:p-5 rounded-2xl transition-all duration-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                  activeStep === i
                    ? "bg-violet-500/[0.06] border border-violet-400/20"
                    : "bg-transparent border border-transparent hover:bg-violet-500/[0.03]"
                }`}
              >
                {/* Step number + icon */}
                <div className="relative flex-shrink-0">
                  <div
                    className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-500 ${
                      activeStep === i
                        ? `bg-gradient-to-br ${step.color} shadow-lg shadow-violet-500/25`
                        : "bg-white/5"
                    }`}
                  >
                    <step.icon
                      className={`w-5 h-5 transition-colors duration-500 ${
                        activeStep === i ? "text-white" : "text-white/40"
                      }`}
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono text-white/25">
                      {step.num}
                    </span>
                    <h3
                      className={`text-base font-semibold transition-colors duration-500 ${
                        activeStep === i ? "text-white" : "text-white/50"
                      }`}
                    >
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-sm text-white/35 leading-relaxed">
                    {step.desc}
                  </p>
                </div>

                {/* Active indicator */}
                {activeStep === i && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-8 bg-gradient-to-b from-violet-400 to-fuchsia-500 rounded-full" />
                )}
              </button>
            ))}
          </div>

          {/* Visual preview card */}
          <div className="hidden lg:block sticky top-28">
            <div className="relative bg-[#12101c] border border-violet-500/12 rounded-2xl p-5 overflow-hidden min-h-[260px]">
              {/* Glow behind */}
              <div className={`absolute -top-20 -right-20 w-40 h-40 rounded-full blur-[80px] transition-colors duration-1000 ${
                activeStep === 0 ? "bg-violet-600/22" :
                activeStep === 1 ? "bg-fuchsia-600/18" :
                activeStep === 2 ? "bg-violet-500/18" :
                "bg-fuchsia-500/16"
              }`} />

              <div className="relative z-10">
                <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-white/25 mb-6">
                  Preview
                </p>

                {/* Step 0: Link */}
                {activeStep === 0 && (
                  <div className="animate-fade-in space-y-4">
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-white/[0.04] border border-white/10">
                      <Link2 className="w-4 h-4 text-violet-400" />
                      <span className="text-sm font-mono text-violet-300">reachmenow.app/yourname</span>
                    </div>
                    <p className="text-xs text-white/30 flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3 text-violet-400" />
                      Replace contact forms, DMs, and emails with one link
                    </p>
                  </div>
                )}

                {/* Step 1: AI chat */}
                {activeStep === 1 && (
                  <div className="animate-fade-in space-y-3">
                    <div className="p-3 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-white/60">
                      "I need a website redesign"
                    </div>
                    <div className="flex gap-2">
                      <div className="w-1 rounded-full bg-gradient-to-b from-violet-500 to-fuchsia-500" />
                      <div className="p-3 rounded-xl bg-violet-500/[0.08] border border-violet-500/15 text-sm text-white/70 flex-1">
                        "Thanks for reaching out! I'd love to help. What's your timeline?"
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-violet-300">
                      <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                      Replied in 1.8 seconds
                    </div>
                  </div>
                )}

                {/* Step 2: Qualification */}
                {activeStep === 2 && (
                  <div className="animate-fade-in space-y-3">
                    <div className="space-y-2">
                      {[
                        { label: "Budget", value: "$10k+", match: true },
                        { label: "Timeline", value: "4 weeks", match: true },
                        { label: "Fit", value: "High", match: true },
                      ].map((q) => (
                        <div key={q.label} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03] border border-white/5">
                          <span className="text-xs text-white/40">{q.label}</span>
                          <span className={`text-xs font-semibold ${q.match ? "text-fuchsia-300" : "text-white/50"}`}>{q.value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded-xl bg-violet-500/[0.1] border border-violet-400/20">
                      <BrainCircuit className="w-4 h-4 text-violet-300" />
                      <span className="text-xs font-semibold text-violet-200">Lead score: 92/100</span>
                    </div>
                  </div>
                )}

                {/* Step 3: Booking */}
                {activeStep === 3 && (
                  <div className="animate-fade-in space-y-4">
                    <div className="p-4 rounded-xl bg-fuchsia-500/[0.08] border border-fuchsia-400/20 text-center">
                      <CalendarCheck className="w-6 h-6 text-fuchsia-300 mx-auto mb-2" />
                      <p className="text-sm font-semibold text-white/80">Meeting Scheduled</p>
                      <p className="text-xs text-white/40 mt-1">Tuesday, 2:00 PM to 2:20 PM</p>
                    </div>
                    <p className="text-[10px] text-center text-white/30">
                      Automatically synced with your calendar
                    </p>
                  </div>
                )}
              </div>

              {/* Progress bar */}
              <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/5">
                <div
                  className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-500"
                  style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
